/**
 * @component CompanyHistoryPage
 * @description 창립부터 현재까지의 주요 이정표를 연도별로 나열하는 회사 연혁 페이지입니다.
 *              연혁 편집은 관리자 콘솔(/admin)에서 수행합니다.
 * @usage App 라우팅에서 `/company/history.html` 경로로 렌더링됩니다.
 */

import { useHistoryTimeline } from '../../hooks/useHistoryTimeline';
import PageHero from '../../components/common/PageHero';
import SectionHeading from '../../components/common/SectionHeading';
import { pageMeta } from '../../constants/navigation';

// [기능] 연혁 배열을 연도별로 그룹화합니다. year 필드는 "YYYY.MM" 또는 "YYYY" 형식입니다.
function groupTimeline(items) {
  const map = new Map();
  for (const item of items) {
    const dotIdx = item.year.indexOf('.');
    const yearPart = dotIdx !== -1 ? item.year.slice(0, dotIdx) : item.year;
    const monthPart = dotIdx !== -1 ? item.year.slice(dotIdx + 1) : '';
    if (!map.has(yearPart)) map.set(yearPart, []);
    map.get(yearPart).push({ month: monthPart ? `${parseInt(monthPart, 10)}월` : '', text: item.text, rawYear: item.year });
  }
  return [...map.entries()];
}

// [기능] 공용 훅으로 조회한 전체 연혁을 연도별로 표시합니다.
export default function CompanyHistoryPage() {
  const { items: historyItems, loading } = useHistoryTimeline();

  const groups = groupTimeline(historyItems);

  return (
    <>
      <PageHero title={pageMeta.companyHistory.title} trail={pageMeta.companyHistory.trail} className="company-page-hero" />
      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="History"
            title="새힘정보기술이 걸어온 주요 이정표"
            description="산업 특화형 스마트 제조 솔루션 파트너로서 구축 경험과 운영 지원 역량을 확장해왔습니다."
            className="fade-in"
          />

          <div className="history">
            {loading ? (
              <p style={{ color: 'rgba(32,35,50,0.5)', fontSize: '0.95rem' }}>로딩 중...</p>
            ) : (
              groups.map(([year, entries], idx) => (
                <div key={year} className={`history-group fade-in${idx === 0 ? ' is-first' : ''}`}>
                  <div className="history-year">{year}</div>
                  <div className="history-list">
                    <ul>
                      {entries.map((entry, i) => (
                        <li key={i}>
                          {entry.month && <span className="history-month">{entry.month}</span>}
                          <span className="history-text">{entry.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </>
  );
}
